"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Building2,
  ChevronsUpDown,
  Pin,
  PinOff,
  CornerDownLeft,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useClientContext } from "@/context/ClientContext";
import type { Doc, Id } from "../../convex/_generated/dataModel";

// ── Avatar ────────────────────────────────────────────────────────────

function ClientAvatar({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const initial = name.charAt(0).toUpperCase();
  return (
    <div
      className={cn(
        "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sidebar-foreground/15 text-xs font-semibold text-sidebar-foreground",
        className,
      )}
    >
      {initial}
    </div>
  );
}

// ── Client row in the list ────────────────────────────────────────────

function ClientRow({
  client,
  isSelected,
  isPinned,
  showPin,
  onSelect,
  onTogglePin,
}: {
  client: Doc<"clients">;
  isSelected: boolean;
  isPinned: boolean;
  showPin: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
        isSelected
          ? "ring-2 ring-primary/40 bg-primary/10 text-foreground"
          : "text-foreground/80 hover:bg-muted",
      )}
    >
      <ClientAvatar name={client.name} className="h-7 w-7 text-[11px]" />
      <span className="min-w-0 flex-1 truncate">{client.name}</span>
      {showPin && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          className={cn(
            "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors",
            isPinned
              ? "text-primary hover:text-primary/70"
              : "text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-foreground",
          )}
          title={isPinned ? "Unpin client" : "Pin client"}
        >
          {isPinned ? (
            <PinOff className="h-3.5 w-3.5" />
          ) : (
            <Pin className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────

export function ClientSwitcher() {
  const {
    clientId,
    setClientId,
    clients,
    currentClient,
    isClientUser,
    isAgencyView,
    loading,
    recentClientIds,
    pinnedClientIds,
    togglePinned,
  } = useClientContext();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // Focus search on open — desktop only; skip on touch devices to avoid keyboard pop-up
  useEffect(() => {
    if (open) {
      const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      if (!isTouchDevice) {
        const id = setTimeout(() => searchInputRef.current?.focus(), 50);
        return () => clearTimeout(id);
      }
    } else {
      setSearch("");
    }
  }, [open]);

  const router = useRouter();
  const pathname = usePathname();

  /**
   * Compute the right destination after switching clients (or to agency view).
   *
   * Context-based pages (/growth-os/*, /tasks, /projects, etc.) re-render
   * automatically via ClientContext — no navigation needed.
   *
   * URL-based pages (/clients/[id]) need a real route change.
   *
   * Transitions to/from agency view may land on pages whose nav sections
   * are hidden, so we redirect to a sensible default.
   */
  const navigateAfterSwitch = useCallback(
    (newClientId: Id<"clients"> | undefined) => {
      const clientDetailMatch = pathname.match(/^\/clients\/([^/]+)(.*)/);
      const isOnAgencyPage =
        pathname.startsWith("/admin") ||
        pathname.startsWith("/clients") ||
        pathname.startsWith("/assessments") ||
        pathname.startsWith("/office") ||
        pathname.startsWith("/memory");
      const isOnClientScopedPage =
        pathname.startsWith("/growth-os") ||
        pathname.startsWith("/tasks") ||
        pathname.startsWith("/projects") ||
        pathname.startsWith("/ideas") ||
        pathname.startsWith("/documents") ||
        pathname.startsWith("/calendar");

      if (newClientId === undefined) {
        // Switching to Agency View
        if (clientDetailMatch) {
          // On a client detail page → go to client list
          router.push("/clients");
        } else if (isOnClientScopedPage) {
          // On a client-scoped page that won't render in agency view → go to dashboard
          router.push("/");
        }
        // Otherwise (dashboard, agency pages) — stay put
      } else {
        // Switching to a specific client
        if (clientDetailMatch) {
          // On /clients/[oldId]/... → navigate to /clients/[newId] (same tab)
          router.push(`/clients/${newClientId}`);
        } else if (isOnAgencyPage && !pathname.startsWith("/clients")) {
          // On an agency-only page (admin, office, memory, assessments) → go to dashboard
          router.push("/");
        }
        // For context-based pages (growth-os, tasks, dashboard) — stay put, context handles it
      }
    },
    [pathname, router],
  );

  const handleSelect = useCallback(
    (id: Id<"clients">) => {
      setClientId(id);
      navigateAfterSwitch(id);
      setOpen(false);
    },
    [setClientId, navigateAfterSwitch],
  );

  const handleSwitchToAgency = useCallback(() => {
    setClientId(undefined);
    navigateAfterSwitch(undefined);
    setOpen(false);
  }, [setClientId, navigateAfterSwitch]);

  // Filtered clients for search
  const filteredClients = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.website?.toLowerCase().includes(q),
    );
  }, [clients, search]);

  // Sort: pinned first, then alphabetical
  const sortedClients = useMemo(() => {
    return [...filteredClients].sort((a, b) => {
      const aPinned = pinnedClientIds.includes(a._id);
      const bPinned = pinnedClientIds.includes(b._id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredClients, pinnedClientIds]);

  // Recent clients
  const recentClients = useMemo(() => {
    return recentClientIds
      .map((id) => clients.find((c) => c._id === id))
      .filter((c): c is Doc<"clients"> => c !== undefined)
      .slice(0, 5);
  }, [recentClientIds, clients]);

  // Loading state
  if (loading) {
    return (
      <div className="mx-3 mt-2 flex items-center gap-2.5 rounded-lg border border-sidebar-border px-3 py-2.5">
        <div className="h-8 w-8 animate-pulse rounded-full bg-sidebar-foreground/10" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 w-24 animate-pulse rounded bg-sidebar-foreground/10" />
          <div className="h-3 w-16 animate-pulse rounded bg-sidebar-foreground/10" />
        </div>
      </div>
    );
  }

  // Display info for the closed-state button
  const displayName = isAgencyView
    ? "Agency View"
    : currentClient?.name ?? "Select a client";
  const displaySubtitle = isAgencyView
    ? "All clients"
    : currentClient?.website ?? "Client workspace";

  return (
    <div ref={containerRef} className="relative mx-3 mt-2">
      {/* ── Trigger button ── */}
      <button
        type="button"
        onClick={() => {
          if (!isClientUser) setOpen((v) => !v);
        }}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-lg border border-sidebar-border px-3 py-2.5 text-left transition-colors",
          isClientUser
            ? "cursor-default"
            : "hover:bg-sidebar-accent/60 active:bg-sidebar-accent",
          open && "bg-sidebar-accent/60",
        )}
      >
        {isAgencyView ? (
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sidebar-foreground/15 text-sidebar-foreground">
            <Building2 className="h-4 w-4" />
          </div>
        ) : (
          <ClientAvatar name={displayName} />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium leading-tight text-sidebar-foreground">
            {displayName}
          </div>
          <div className="mt-0.5 truncate text-xs leading-tight text-sidebar-foreground/60">
            {displaySubtitle}
          </div>
        </div>
        {!isClientUser && (
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/60" />
        )}
      </button>

      {/* ── Popover ── */}
      {open && !isClientUser && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
          {/* Search */}
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-lg bg-muted/60 px-2.5">
              <Search className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for a client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto p-1.5">
            {/* Switch to Agency View */}
            {!isAgencyView && !search.trim() && (
              <button
                type="button"
                onClick={handleSwitchToAgency}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-foreground/80 transition-colors hover:bg-muted"
              >
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <CornerDownLeft className="h-3.5 w-3.5" />
                </div>
                <span>Switch to Agency View</span>
              </button>
            )}

            {/* Recent section */}
            {!search.trim() && recentClients.length > 0 && (
              <>
                <div className="mb-1 mt-2 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Recent
                </div>
                {recentClients.map((client) => (
                  <ClientRow
                    key={`recent-${client._id}`}
                    client={client}
                    isSelected={client._id === clientId}
                    isPinned={pinnedClientIds.includes(client._id)}
                    showPin={false}
                    onSelect={() => handleSelect(client._id)}
                    onTogglePin={() => togglePinned(client._id)}
                  />
                ))}
              </>
            )}

            {/* All Clients section */}
            <div className="mb-1 mt-2 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {search.trim() ? "Results" : "All Clients"}
            </div>
            {sortedClients.length === 0 ? (
              <div className="px-2.5 py-4 text-center text-xs text-muted-foreground">
                {search.trim()
                  ? "No clients match your search."
                  : "No clients found."}
              </div>
            ) : (
              sortedClients.map((client) => (
                <ClientRow
                  key={client._id}
                  client={client}
                  isSelected={client._id === clientId}
                  isPinned={pinnedClientIds.includes(client._id)}
                  showPin={true}
                  onSelect={() => handleSelect(client._id)}
                  onTogglePin={() => togglePinned(client._id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
